import type { EntityUser } from '../types';

type AvatarSize = 'sm' | 'md' | 'lg' | 'xl';

interface AvatarProps {
  user: Pick<EntityUser, 'fullName' | 'profilePictureUrl'> | null | undefined;
  size?: AvatarSize;
  className?: string;
}

const sizeClasses: Record<AvatarSize, string> = {
  sm: 'h-7 w-7 text-micro',
  md: 'h-9 w-9 text-meta',
  lg: 'h-12 w-12 text-base',
  xl: 'h-16 w-16 text-xl',
};

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return (parts[0]?.[0] ?? '?').toUpperCase();
  return `${parts[0]?.[0] ?? ''}${parts[parts.length - 1]?.[0] ?? ''}`.toUpperCase();
}

function getColorFromName(name: string): string {
  const colors = [
    'bg-blue-500',
    'bg-violet-500',
    'bg-emerald-500',
    'bg-amber-500',
    'bg-rose-500',
    'bg-cyan-500',
    'bg-indigo-500',
    'bg-teal-500',
  ];
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return colors[Math.abs(hash) % colors.length] ?? 'bg-blue-500';
}

export function Avatar({ user, size = 'md', className = '' }: AvatarProps) {
  const sizeClass = sizeClasses[size];
  const name = user?.fullName ?? 'Unknown';

  if (user?.profilePictureUrl) {
    return (
      <img
        src={user.profilePictureUrl}
        alt={name}
        className={`${sizeClass} rounded-full object-cover flex-shrink-0 ${className}`}
      />
    );
  }

  const colorClass = getColorFromName(name);
  const initials = getInitials(name);

  return (
    <div
      className={`${sizeClass} ${colorClass} rounded-full flex items-center justify-center font-medium text-white flex-shrink-0 ${className}`}
      title={name}
    >
      {initials}
    </div>
  );
}
